import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useT } from "../lib/i18n";
import { Button, Modal, RoomCode } from "./ui";

/** Copy with visible confirmation — a silent copy reads as a broken button. */
function CopyButton({ value }: { value: string }) {
  const t = useT();
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!done) return;
    const timer = setTimeout(() => setDone(false), 1600);
    return () => clearTimeout(timer);
  }, [done]);

  return (
    <Button
      size="sm"
      icon={done ? "check" : "copy"}
      className={done ? "text-success" : undefined}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
        } catch {
          // Blocked without a secure context or permission. The URL is on
          // screen anyway, so this is a nicety, not a dependency.
        }
      }}
    >
      {done ? t("copied") : t("copy")}
    </Button>
  );
}

/**
 * The invite: room code, QR to the player screen, and the raw link for anyone
 * whose camera will not cooperate. The QR encodes whatever origin served this
 * page, so it is correct on localhost and in production alike.
 */
export function ShareCard({ code, size = 224 }: { code: string; size?: number }) {
  const t = useT();
  const joinUrl = `${window.location.origin}/r/${code}/play`;

  return (
    <div className="w-full max-w-sm mx-auto flex flex-col items-center gap-5 text-center">
      <div>
        <div className="type-eyebrow text-ink/40">{t("roomCode")}</div>
        <RoomCode code={code} size="xl" className="text-accentLight" />
      </div>

      <div className="bg-white p-4 rounded-card shadow-pop">
        <QRCodeSVG value={joinUrl} size={size} includeMargin={false} />
      </div>

      <p className="text-ink/45 text-sm">{t("scanToJoin")}</p>

      <div className="flex items-center gap-2 max-w-full min-w-0 w-full">
        <code className="flex-1 min-w-0 text-sm text-ink/55 truncate bg-surface2 rounded-control px-3 py-2">
          {joinUrl}
        </code>
        <CopyButton value={joinUrl} />
      </div>
    </div>
  );
}

/** Full-screen version, for the host's "everyone scan this now" moment. */
export function ShareModal({ code, onClose }: { code: string; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} width="max-w-sm">
      <ShareCard code={code} size={240} />
    </Modal>
  );
}
